<template>
  <div class="rfr-visits-view">
    <NewVisitFormModal
      v-if="newVisitFormVisible"
      @closeModal="newVisitFormVisible = false"
    />
    <NewVisitsView />
    <div v-if="allowCloseVisits" class="rfr-visits-view__extra-instructions">
      <p>
        <span
          >Select 1 or more Inspection Reports below and click
          <b>'Close Checked Visits'</b>. EIRs can be closed from this grid if
          they contain no *** informative *** information.
        </span>
      </p>
    </div>
    <div v-if="instruction" class="rfr-visits-view__instructions">
      <p>{{ instruction }}</p>
    </div>
    <div class="rfr-visits-view__navigation">
      <Button
        type="button"
        theme-color="dark"
        @click="onPreviousClicked"
        data-cy="previous-button"
        >Previous</Button
      >
      <Button
        v-if="
          mode === EnumVisitMode.Financial || mode === EnumVisitMode.Payable
        "
        type="button"
        theme-color="dark"
        @click="onNextClicked"
        data-cy="next-button"
        >Next</Button
      >
    </div>
    <RfrVisitsTable
      :mode="mode"
      :menuType="menuType"
      :allowCloseVisits="allowCloseVisits"
      :tripVisits="tripVisits"
      ref="visitsTableRef"
    />
    <span
      v-if="menuType == 'VisitsAsRfr' && mode == EnumVisitMode.Inspection"
      class="rfr-visits-view__new-visit"
    >
      <span> If you do not have a pre-assigned Visit ID </span>
      <a @click="newVisitFormVisible = true"> Click Here >> </a>
      <span> to submit & attach an inspection report</span>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import { useMainStore } from "@/stores/useMainStore";
import RfrVisitsTable from "./RfrVisitsTable.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import { EnumVisitMode } from "@/Domain/EnumVisitMode";
import NewVisitFormModal from "@/views/inspections/NewVisitFormModal.vue";
import NewVisitsView from "@/views/inspections/NewVisitsView.vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const urlParams = new URLSearchParams(window.location.search);
const mode =
  (Number(urlParams.get("type")) in EnumVisitMode
    ? (Number(urlParams.get("type")) as EnumVisitMode)
    : EnumVisitMode[urlParams.get("type") as keyof typeof EnumVisitMode]) ??
  EnumVisitMode.Inspection;
const menuType = urlParams.get("menu") ?? "VisitsAsRfr";

const visitsTableRef = ref<InstanceType<typeof RfrVisitsTable>>();
const newVisitFormVisible = ref(false);
const tripVisits = ref<number[]>([]);

const instructions = {
  [EnumVisitMode.Inspection]: "",
  [EnumVisitMode.Financial]:
    "Select visit(s) for payment/reimbursement. Visits will only be available for 90 days.",
  [EnumVisitMode.Historical]: "",
  [EnumVisitMode.Overdue]: "",
  [EnumVisitMode.Payable]: "",
};

const instruction = instructions[mode];

const allowCloseVisits = computed(() => {
  return menuType === "VisitsAsRc" && mode !== EnumVisitMode.Historical;
});

const onPreviousClicked = function () {
  window.location.href =
    menuType === "VisitsAsRc"
      ? "../DefaultInspections?VisitsForRole=RC"
      : "../DefaultInspections";
};

const getTripIdAsync = async function () {
  let tripId = -1;

  await handleResponsePromiseAsync(
    api.get("GetTripId", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
    }),
    (data) => {
      tripId = data;
    },
    "RfrVisitsView.vue",
    "getTripIdAsync"
  );

  return tripId;
};

const updateTripVisitsAsync = async function (
  includedVisitIds: number[],
  excludedVisitIds: number[]
) {
  return handleResponsePromiseAsync(
    api.post("UpdateTripVisits", {
      includedVisitIds: includedVisitIds,
      excludedVisitIds: excludedVisitIds,
    }),
    () => {},
    "RfrVisitsView.vue",
    "updateTripVisitsAsync"
  );
};

const getTripVisitsAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetTripVisits", {
      tripId: 0,
      newTrip: true,
    }),
    (data) => {
      tripVisits.value = data.map((v) => v.VisitId);
    },
    "RfrVisitsView.vue",
    "getTripVisitsAsync"
  );
};

const onNextClicked = async function () {
  // Validation added that all visits belong to a single RFR (because RFR Team Leaders see a list which could include multiple RFRs)
  const visits = visitsTableRef.value?.visits ?? [];
  const selectedVisits = visits.filter((v) => v.selected);
  const selectedRfrNames = selectedVisits
    ?.map((v) => v.RfrName)
    .filter((v, i, a) => a.indexOf(v) === i);

  // If there are 0 rows selected or more than one rfr selected then the user cant continue
  if (mode == EnumVisitMode.Financial && selectedRfrNames.length != 1) {
    notificationStore.notify({
      message:
        "Trips must contain at least one visit and are limited to one RFR only.",
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
    return;
  }

  const tripId = await getTripIdAsync();
  if (tripId > 0) {
    // updateVisitsInTrip
    await updateTripVisitsAsync(
      selectedVisits.map((v) => v.Id),
      visits.filter((v) => !v.selected).map((v) => v.Id)
    );

    window.location.href = `EFRTripExpenses?tripID=${tripId}&type=${EnumVisitMode[mode]}&mode=new&RFRID=${main.userLogin}&menu=${menuType}`;
  }
};

onBeforeMount(() => {
  if (mode === EnumVisitMode.Financial) getTripVisitsAsync();
});
</script>

<style scoped lang="scss">
.rfr-visits-view {
  margin: 10px 30px 30px;

  &__extra-instructions {
    margin-bottom: 20px;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    background-color: #f5f5f5;
  }

  &__instructions {
    margin-bottom: 20px;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    background-color: #f5f5f5;
  }

  &__navigation {
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
  }

  &__new-visit {
    margin-top: 20px;
    display: block;
    font-size: 15px;
    font-weight: bold;

    a {
      cursor: pointer;

      &:hover {
        color: #007bff;
      }
    }
  }
}
</style>
